(ns app.front.view.property.units-editor
  (:require [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.select :as ui-select]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.util :as futil]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn- reset-new-unit-form [db]
  (-> db
      (dissoc ::new-unit-number ::new-unit-designator ::new-unit-property)
      (assoc ::new-unit-section-open? false)))

(rf/reg-event-db ::set-new-unit-section-open
  (fn [db [_ open?]]
    (-> (assoc db ::new-unit-section-open? open?)
        (dissoc ::new-unit-number ::new-unit-designator ::new-unit-property))))

;; Closes and clears the new-unit form without staging anything, for callers (e.g. the application
;; wizard) that recognize the entered unit already exists and handle selecting it themselves.
(rf/reg-event-db ::close-new-unit-form
  (fn [db _]
    (reset-new-unit-form db)))

(rf/reg-event-db ::save-new-unit-number
  (fn [db [_ unit-number]]
    (assoc db ::new-unit-number unit-number)))

(rf/reg-event-db ::select-new-unit-designator
  (fn [db [_ unit-designator]]
    (assoc db ::new-unit-designator unit-designator)))

(rf/reg-event-db ::select-new-unit-property
  (fn [db [_ property]]
    (assoc db ::new-unit-property property)))

(rf/reg-sub ::new-unit-section-open?
  :-> ::new-unit-section-open?)

(rf/reg-sub ::new-unit-number
  :-> ::new-unit-number)

(rf/reg-sub ::new-unit-designator
  :-> ::new-unit-designator)

(rf/reg-sub ::new-unit-property
  :-> ::new-unit-property)

(rf/reg-event-db ::set-pending-removal-unit-ids
  (fn [db [_ target-id unit-ids]]
    (if (seq unit-ids)
      (assoc-in db [::pending-removal-unit-ids target-id] unit-ids)
      (p/dissoc-in db [::pending-removal-unit-ids target-id]))))

(rf/reg-sub ::pending-removal-unit-ids
  (fn [db [_ target-id]]
    (get-in db [::pending-removal-unit-ids target-id])))

(rf/reg-sub ::property-write-forbidden?
  (fn [_ [_ property]]
    (not (-> property :granted-permissions :permission.property/write boolean))))

(rf/reg-sub ::action-disabled?
  (fn [[_ target-id property signal] _]
    (if target-id
      (rf/subscribe [::process/signal-forbidden? target-id [signal]])
      (rf/subscribe [::property-write-forbidden? property])))
  (fn [forbidden? _]
    forbidden?))

(defn- duplicate-pending-unit?
  "True if `pending-units` (each with `:identifier` and `:unit-designator`, as staged in
  `::pending-new-property-units`) already contains an entry matching the given `identifier` and
  `unit-designator`."
  [pending-units identifier unit-designator]
  (let [target [(str-util/blank->nil identifier) (:db/id unit-designator)]]
    (boolean (some (fn [unit]
                     (= target [(str-util/blank->nil (:identifier unit))
                                (:db/id (:unit-designator unit))]))
                   pending-units))))

(rf/reg-event-fx ::add-new-property-unit
  (fn [{:keys [db]} [_ target-id property-id identifier unit-designator]]
    (let [unit-designator-id (:db/id unit-designator)
          db                 (reset-new-unit-form db)]
      (cond
        target-id
        {:dispatch [:app.front.process/signal
                    target-id
                    [:process.signal/add-property-unit (p/assoc-if
                                                         (util/mmap property-id unit-designator-id)
                                                         :identifier identifier)]
                    {:notify {:success :workspace/property-unit-added}}]
         :db       db}

        property-id
        {:comm/command {:command    :property/add-property-unit
                        :method     :post
                        :data       (p/assoc-if {:property-id   property-id
                                                 :designator-id unit-designator-id}
                                                :identifier (when-not (str/blank? identifier)
                                                              identifier))
                        :on-success [::entities/apply-changes]
                        :notify     {:success :workspace/property-unit-added}}
         :db           db}

        ;; When called from the wizard no target-id exists (due to the application not having been created yet)
        ;; nor is there a property-id (due to the wizard unit selection using property stand-ins). Add properties
        ;; to ::pending-new-property-units instead.
        :else
        (if (duplicate-pending-unit? (::pending-new-property-units db) identifier unit-designator)
          {:db db}
          {:db (update db ::pending-new-property-units (fnil conj [])
                       (p/assoc-if {:unit-designator unit-designator} :identifier identifier))})))))

(rf/reg-sub ::pending-new-property-units
  :-> ::pending-new-property-units)

(rf/reg-event-db ::clear-pending-new-property-units
  (fn [db _]
    (dissoc db ::pending-new-property-units)))

(rf/reg-event-db ::remove-pending-new-property-unit
  (fn [db [_ unit]]
    (update db ::pending-new-property-units
            (fn [units] (vec (remove #(= % unit) units))))))

(rf/reg-event-fx ::remove-property-unit
  (fn [_ [_ target-id unit-id]]
    (if target-id
      {:dispatch [:app.front.process/signal
                  target-id
                  [:process.signal/remove-property-unit (util/mmap unit-id)]
                  {:notify {:success :workspace/property-unit-removed}}]}
      {:comm/command {:command    :property/delete-property-unit
                      :method     :post
                      :data       (util/mmap unit-id)
                      :on-success [::entities/apply-changes]
                      :notify     {:success :workspace/property-unit-removed}}})))

(rf/reg-event-fx ::verify-property-unit
  (fn [_ [_ target-id unit-id]]
    (if target-id
      {:dispatch [:app.front.process/signal
                  target-id
                  [:process.signal/verify-property-unit (util/mmap unit-id)]
                  {:notify {:success :workspace/property-unit-verified}}]}
      {:comm/command {:command    :property/verify-property-unit
                      :method     :post
                      :data       (util/mmap unit-id)
                      :on-success [::entities/apply-changes]
                      :notify     {:success :workspace/property-unit-verified}}})))

(defn- info-field [tr property attr]
  [:div.mb-2
   [:div.text-xs.font-semibold.text-gray-600 (tr [attr])]
   [:div.mt-1.text-sm.text-black (or (get property attr) "—")]])

(defn read-only-info-columns [{:keys [tr property label]}]
  [:div.border.border-solid.border-gray-400.rounded.p-4.mb-4
   (when label
     [:h3.mb-3.font-bold label])
   [:div.grid.grid-cols-3.gap-4
    [:div
     [:h4.mb-2.font-bold (tr [:property-view/property-address])]
     [:div.text-sm.text-black (or (-> property :property/primary-address :address/full-address) "—")]]
    [:div
     [:h4.mb-2.font-bold (tr [:property-view/property-details])]
     [info-field tr property :property/integration-id]
     [info-field tr property :property/type-code]
     [info-field tr property :property/ward]]
    [:div
     [:h4.mb-2.font-bold (tr [:property-view/other-information])]
     [info-field tr property :property/zoning]
     [info-field tr property :property/legal]]]])

(defn read-only-info-columns-list [{:keys [tr workspace]}]
  (let [properties (->> (:workspace/properties workspace)
                        (sort-by (juxt (workspace-model/is-secondary-property-fn workspace)
                                       :property/id)))
        multi?     (> (count properties) 1)]
    [:div
     (futil/for-all-indexed [[idx property] properties]
       ^{:key (:db/id property)}
       [read-only-info-columns
        {:tr       tr
         :property property
         :label    (when multi?
                     (tr [:property-view/property-label] [(inc idx)]))}])]))

(defn matching-existing-unit
  "Returns the entity from `existing-units` (each with `:property-unit/identifier` and
  `:property-unit/designator`) whose identifier+designator match the given `identifier` and
  `unit-designator`, or nil. Lets callers (e.g. the application wizard) recognize when a
  manually-entered new unit is actually an already-registered one, so it can be selected
  instead of staged as new."
  [existing-units identifier unit-designator]
  (let [target [(str-util/blank->nil identifier) (:db/id unit-designator)]]
    (some (fn [unit]
            (when (= target [(str-util/blank->nil (:property-unit/identifier unit))
                             (:db/id (:property-unit/designator unit))])
              unit))
          existing-units)))

(defn new-unit-fields
  "Renders the open new-unit form (address/property select, unit number, designator, cancel/add buttons).
  Public so the application wizard can embed just the form, triggered by its own custom button.

  `layout` can be `:narrow`, `:responsive`, or `:wide`. Narrow puts the address field on its own row
  above the unit number/designator row. Responsive uses narrow below the `xl` breakpoint and wide above it.

  `on-add`, if given, is called with `[property identifier unit-designator]` when the add button is
  clicked, instead of dispatching `::add-new-property-unit`. Return truthy to indicate the add was
  handled (e.g. by selecting a matching existing unit instead of staging a new one); returning falsy
  falls back to the default `::add-new-property-unit` dispatch."
  [tr lang target-id properties designators & {:keys [layout on-add] :or {layout :wide}}]
  (let [multi?              (> (count properties) 1)
        unit-number-val     (<sub [::new-unit-number])
        unit-designator-val (<sub [::new-unit-designator])
        selected-property   (<sub [::new-unit-property])
        pending-units       (<sub [::pending-new-property-units])
        duplicate-pending?  (boolean (and unit-designator-val
                                          (duplicate-pending-unit? pending-units unit-number-val unit-designator-val)))
        ;; Default to primary property when single
        property            (if multi? selected-property (first properties))
        property-address    (some-> property property-model/full-address)
        address-field       (if multi?
                              [ui-select/select {:id           "unit-property-select"
                                                 :label-text   (tr [:workspace/address])
                                                 :required     true
                                                 :values       properties
                                                 :val->str     #(or (property-model/full-address %) "—")
                                                 :on-change    #(>evt [::select-new-unit-property %])
                                                 :value        selected-property
                                                 :allow-empty? false}]
                              [ui/text-input {:id          "unit-address-reminder"
                                              :label-text  (tr [:workspace/address])
                                              :required    false
                                              :placeholder property-address
                                              :disabled    true
                                              :value       property-address}])
        number-field        [ui/text-input {:id           "unit-number-text-input"
                                            :label-text   (tr [:workspace/unit-number])
                                            :disabled     false
                                            :value        unit-number-val
                                            :invalid      duplicate-pending?
                                            :invalid-text (tr [:workspace/duplicate-pending-unit])
                                            :on-change    #(>evt [::save-new-unit-number (futil/tval %)])}]
        designator-field    [ui-select/select {:id           "unit-type-select"
                                               :label-text   (tr [:workspace/unit-designator])
                                               :required     true
                                               :values       designators
                                               :val->str     #(property-model/unit-designator-name % lang)
                                               :on-change    #(>evt [::select-new-unit-designator %])
                                               :value        unit-designator-val
                                               :allow-empty? false}]]
    [:div.flex.flex-col.border-2.border-solid.border-ui-blue-100.rounded-md.bg-ui-blue-10
     (case layout
       :narrow
       [:div.m-4
        [:div.flex.flex-row.gap-2 address-field]
        [:div.flex.flex-row.gap-2.mt-2 number-field designator-field]]

       :responsive
       [:<>
        [:div.m-4.xl:hidden
         [:div.flex.flex-row.gap-2 address-field]
         [:div.flex.flex-row.gap-2.mt-2 number-field designator-field]]
        [:div.hidden.xl:flex.flex-row.gap-2.m-4 address-field number-field designator-field]]

       :wide
       [:div.flex.flex-row.gap-2.m-4 address-field number-field designator-field]

       (throw (ex-info "Unknown new-unit form layout" {:layout layout})))
     [:div.flex.flex-row.gap-2.m-4
      [ui/button
       {:kind     :secondary
        :size     :field
        :on-click #(>evt [::set-new-unit-section-open false])}
       (tr [:cancel])]
      [ui/button
       {:kind     :primary
        :size     :field
        :disabled (or (nil? unit-designator-val)
                      (nil? property)
                      duplicate-pending?)
        :on-click #(when-not (and on-add (on-add property unit-number-val unit-designator-val))
                     (>evt [::add-new-property-unit target-id (:db/id property) unit-number-val unit-designator-val]))}
       (tr [:add])]]]))

(defn- new-unit-section
  [tr lang target-id properties designators & {:keys [inline? layout on-add] :or {layout :wide}}]
  (let [open?    (<sub [::new-unit-section-open?])
        property (if (> (count properties) 1) (<sub [::new-unit-property]) (first properties))]
    (if open?
      [new-unit-fields tr lang target-id properties designators
       :layout  layout
       :on-add  on-add]
      [:div {:class (when-not inline? "py-4")}
       [ui/button
        {:kind     :secondary
         :size     :field
         :disabled (or (empty? properties)
                       (<sub [::action-disabled? target-id property :process.signal/add-property-unit]))
         :on-click #(>evt [::set-new-unit-section-open true])}
        (tr [:process.signal/add-property-unit])]])))

(defn added-units-list
  "Listing of unit number + designator (no state column), for reviewing units already added in the
  current session, with an option to remove a unit again via `on-remove`. Public so the application
  wizard can show a combined summary of both selected existing units and newly created (not yet
  persisted) units."
  [tr lang units & {:keys [on-remove]}]
  [sortable-table/table
   (p/assoc-if
     {:id-func       (fn [unit] (or (:db/id unit) (:property-unit/identifier unit) (hash unit)))
      :cols          [[(tr [:workspace/unit-number]) :property-unit/identifier {:sorting true}]
                      [(tr [:workspace/unit-designator])
                       #(-> % :property-unit/designator (property-model/unit-designator-name lang))
                       {:sorting true}]]
      :items         units
      :configurable? false
      :tr            tr}
     :make-actions (when on-remove
                     (fn [unit]
                       [{:title    (tr [:remove])
                         :on-click #(on-remove unit)}])))])

(defn- existing-units-selector
  [{:keys [tr lang target-id units property-units disabled? add-existing-unit!]}]
  (when target-id
    (let [display-key              (fn [unit]
                                     (property-model/localized-unit-display-key
                                       unit
                                       #(property-model/unit-designator-name % lang)))
          workspace-unit-ids       (du/entity-id-set units)
          pending-removal-unit-ids (<sub [::pending-removal-unit-ids target-id])]
      [:<>
       ^{:key (str "available-property-units-" (hash [workspace-unit-ids pending-removal-unit-ids]))}
       [ui-ms/multi-select
        {:id             "available-property-units-multi-select"
         :class-name     "w-96 flex-shrink-0"
         :label-text     (tr [:wizard/available-units])
         :displayed-text (tr [(if (seq property-units)
                                :wizard/select-units
                                :wizard/no-existing-units-found)])
         :disabled       disabled?
         :values         property-units
         :value          (set (filter #(contains? workspace-unit-ids (:db/id %)) property-units))
         :val->str       display-key
         :on-change      (fn [selected]
                           (let [selected-ids     (du/entity-id-set selected)
                                 added-unit-ids   (set/difference selected-ids workspace-unit-ids)
                                 removed-unit-ids (set/difference workspace-unit-ids selected-ids)]
                             (doseq [unit (filter #(contains? added-unit-ids (:db/id %)) property-units)]
                               (add-existing-unit! unit))
                             (>evt [::set-pending-removal-unit-ids target-id removed-unit-ids])))}]
       [ui/confirm-modal
        {:id           "remove-property-unit-confirmation"
         :tr           tr
         :open?        (boolean (seq pending-removal-unit-ids))
         :on-close     #(>evt [::set-pending-removal-unit-ids target-id nil])
         :on-yes-click #(do (doseq [unit-id pending-removal-unit-ids]
                              (>evt [::remove-property-unit target-id unit-id]))
                            (>evt [::set-pending-removal-unit-ids target-id nil]))}
        (tr [(if (> (count pending-removal-unit-ids) 1)
               :workspace/remove-property-units-confirm
               :workspace/remove-property-unit-confirm)])]])))

(defn- selectable-property-units [properties lang]
  (->> properties
       (mapcat property-model/active-property-units)
       (sort-by #(property-model/localized-unit-display-key
                   %
                   (fn [designator]
                     (property-model/unit-designator-name designator lang))))))

(defn- units-list [tr lang target-id property units editable? property-manager?]
  (let [table-props {:id              (str "workspace-units-table-" (:db/id property))
                     :id-func         :property-unit/display-key
                     :initial-sorting [:property-unit/identifier :asc]
                     :cols            [[(tr [:workspace/unit-number]) :property-unit/identifier {:sorting true}]
                                       [(tr [:workspace/unit-designator])
                                        #(-> % :property-unit/designator (property-model/unit-designator-name lang))
                                        {:sorting true}]
                                       [(tr [:property-view/state]) (fn [unit] (tr [(:domain/state unit)])) {:sorting true}]]
                     :items           units
                     :configurable?   false
                     :get-status      #(case (:domain/state %)
                                         :property-unit.state/active :ok
                                         :property-unit.state/unverified :warning
                                         nil)
                     :tr              tr}]
    [sortable-table/table
     (p/assoc-if table-props
                 :make-actions (when editable?
                                 (fn [unit]
                                   (cond-> [{:title           (tr [:remove])
                                             :disabled?       (<sub [::action-disabled?
                                                                     target-id
                                                                     property
                                                                     :process.signal/remove-property-unit])
                                             :confirm?        true
                                             :confirm-content (tr [:workspace/remove-property-unit-confirm])
                                             :on-click        #(>evt [::remove-property-unit target-id (:db/id unit)])}]
                                     (and property-manager?
                                          (= :property-unit.state/unverified
                                             (:domain/state unit)))
                                     (conj {:title           (tr [:process.signal/verify-property-unit])
                                            :disabled?       (<sub [::action-disabled? target-id property :process.signal/verify-property-unit])
                                            :confirm?        true
                                            :confirm-content (tr [:property-unit/verify-confirm])
                                            :on-click        #(>evt [::verify-property-unit target-id (:db/id unit)])})))))]))

(defn editor-list [{:keys [tr target-id properties units designators editable?]}]
  (let [lang               (<sub [:language])
        property-manager?  (<sub [:session/property-manager?])
        new-unit-open?     (<sub [::new-unit-section-open?])
        property-units     (selectable-property-units properties lang)
        add-existing-unit! (fn [unit]
                             (>evt [::process/signal target-id
                                    [:process.signal/add-property-unit
                                     (p/assoc-if
                                       {:property-id        (-> unit :property-unit/property :db/id)
                                        :unit-designator-id (-> unit :property-unit/designator :db/id)}
                                       :identifier (:property-unit/identifier unit))]
                                    {:notify {:success :workspace/property-unit-added}}]))
        units-by-prop      (group-by #(let [prop (:property-unit/property %)]
                                        (if (number? prop) prop (:db/id prop)))
                                     units)
        props-with-units   (->> properties
                                (keep-indexed (fn [idx property]
                                                (when-let [units (seq (get units-by-prop (:db/id property)))]
                                                  {:property property
                                                   :units    units
                                                   :index    (inc idx)}))))
        multi?             (> (count properties) 1)]
    [:div.flex.flex-col
     [:div.section-heading
      [:h2 (tr [:workspace/units-on-property])]]
     (when (empty? props-with-units)
       [:div.flex
        [ui/tag {:color      :light-gray
                 :type       :round
                 :class-name "m-4"}
         (tr [:property/no-units-info-text])]])
     (when editable?
       [:div.flex.gap-4.mb-4
        {:class (if new-unit-open? "items-center" "items-end")}
        [existing-units-selector
         {:tr                 tr
          :lang               lang
          :target-id          target-id
          :units              units
          :property-units     property-units
          :disabled?          (or (empty? property-units)
                                  (<sub [::action-disabled?
                                         target-id
                                         (first properties)
                                         :process.signal/add-property-unit]))
          :add-existing-unit! add-existing-unit!}]
        [:div.flex.items-center.gap-4
         (when target-id
           [:div.font-bold.uppercase.text-ui-night-sky-100 (tr [:wizard/or])])
         [new-unit-section tr lang target-id properties designators
          :inline? true
          :layout  :responsive
          :on-add  (fn [property identifier unit-designator]
                     (when-let [existing (matching-existing-unit
                                           (filter #(= (:db/id property)
                                                       (-> % :property-unit/property :db/id))
                                                   property-units)
                                           identifier
                                           unit-designator)]
                       (>evt [::set-new-unit-section-open false])
                       (add-existing-unit! existing)
                       true))]]])
     (doall
       (for [{:keys [property units index]} props-with-units]
         ^{:key (:db/id property)}
         [:div
          (when multi?
            [:h3.mt-4.mb-2.font-bold (tr [:property-view/property-label] [index])])
          [units-list tr lang target-id property units editable? property-manager?]]))]))
